import React, { useMemo, useState } from "react";
import Layout, { PageHeader } from "../components/Layout";
import api, { formatApiError } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import {
  Sparkle, MagnifyingGlass, ShoppingCart, Trash, CheckCircle, XCircle, Crown, Warning,
} from "@phosphor-icons/react";

const STYLES = ["brandable", "short", "keyword_rich", "professional", "premium"];
const TLDS = [".com", ".io", ".co", ".net", ".org", ".in", ".ai", ".app", ".dev"];
const CATEGORY_LABELS = {
  brandable: "Brandable",
  short: "Short & Catchy",
  keyword_rich: "Keyword-Rich",
  professional: "Professional",
  premium: "Premium",
};

export default function BuyDomains() {
  const [form, setForm] = useState({
    description: "",
    keywords: "",
    niche: "",
    styles: ["brandable", "short"],
    tlds: [".com", ".io"],
    count: 20,
  });
  const [generating, setGenerating] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [availability, setAvailability] = useState({}); // {domain: result}
  const [checking, setChecking] = useState(false);
  const [manualInput, setManualInput] = useState("");
  const [cart, setCart] = useState({}); // { domain: { years, whois_guard } }
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseResult, setPurchaseResult] = useState(null);

  const toggleIn = (key, value) => {
    setForm((f) => ({ ...f, [key]: f[key].includes(value) ? f[key].filter((x) => x !== value) : [...f[key], value] }));
  };

  const generate = async () => {
    if (!form.description.trim()) {
      toast.error("Please describe your business or project");
      return;
    }
    setGenerating(true);
    setSuggestions([]);
    setAvailability({});
    try {
      const { data } = await api.post("/domain-suggestions/generate", {
        description: form.description,
        keywords: form.keywords.split(",").map((k) => k.trim()).filter(Boolean),
        niche: form.niche,
        styles: form.styles,
        tlds: form.tlds,
        count: Number(form.count) || 20,
      });
      setSuggestions(data.suggestions || []);
      toast.success(`${data.suggestions?.length || 0} names generated`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setGenerating(false); }
  };

  const runAvailability = async (list) => {
    if (!list.length) return;
    setChecking(true);
    try {
      const { data } = await api.post("/domain-suggestions/availability", { domains: list });
      const next = { ...availability };
      (data.results || []).forEach((r) => { next[r.domain.toLowerCase()] = r; });
      setAvailability(next);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setChecking(false); }
  };

  const checkAllSuggestions = () => runAvailability(suggestions.map((s) => s.domain));
  const checkManual = () => {
    const list = manualInput.split(/[\s,\n]+/).map((x) => x.trim().toLowerCase()).filter(Boolean);
    if (!list.length) { toast.error("Enter at least one domain"); return; }
    // also add to suggestions list visually
    setSuggestions((prev) => {
      const existing = new Set(prev.map((s) => s.domain));
      const added = list.filter((d) => !existing.has(d)).map((d) => ({
        domain: d, category: "manual", rationale: "Manually entered",
      }));
      return [...prev, ...added];
    });
    runAvailability(list);
    setManualInput("");
  };

  const grouped = useMemo(() => {
    const g = {};
    suggestions.forEach((s) => {
      (g[s.category] = g[s.category] || []).push(s);
    });
    return g;
  }, [suggestions]);

  const addToCart = (domain) => {
    setCart((c) => ({ ...c, [domain]: c[domain] || { years: 1, whois_guard: true } }));
    toast.success(`${domain} added to cart`);
  };
  const removeFromCart = (domain) => {
    setCart((c) => { const n = { ...c }; delete n[domain]; return n; });
  };
  const updateCart = (domain, patch) => {
    setCart((c) => ({ ...c, [domain]: { ...c[domain], ...patch } }));
  };

  const purchase = async () => {
    const items = Object.entries(cart).map(([domain, v]) => ({ domain, years: v.years, whois_guard: v.whois_guard }));
    if (!items.length) return;
    setPurchasing(true);
    setPurchaseResult(null);
    try {
      const { data } = await api.post("/orders/purchase", { items });
      setPurchaseResult(data);
      if (data.purchase_status === "success") toast.success("Purchase complete");
      else if (data.purchase_status === "partial") toast.warning("Some domains failed");
      else toast.error("Purchase failed");
      if (data.purchase_status !== "failed") setCart({});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setPurchasing(false); }
  };

  const availBadge = (d) => {
    const r = availability[d.toLowerCase()];
    if (!r) return <StatusBadge>unchecked</StatusBadge>;
    if (r.error_no && r.error_no !== "0") return <StatusBadge variant="danger">error</StatusBadge>;
    if (r.is_premium) return <StatusBadge variant="premium" icon={Crown}>premium</StatusBadge>;
    if (r.available) return <StatusBadge variant="success" icon={CheckCircle}>available</StatusBadge>;
    return <StatusBadge variant="danger" icon={XCircle}>taken</StatusBadge>;
  };

  const cartDomains = Object.keys(cart);

  return (
    <Layout>
      <PageHeader
        eyebrow="Discovery"
        title="Buy Domains"
        description="Describe your project, generate AI-powered names, check availability in bulk, and register — all in one place."
      />

      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4">
        {/* Left: Generator form */}
        <Card className="p-6 h-fit lg:sticky lg:top-20" data-testid="ai-generator-card">
          <div className="flex items-center gap-2 mb-4">
            <Sparkle size={18} weight="fill" className="text-primary" />
            <h3 className="font-display text-lg font-bold tracking-tight">AI Name Generator</h3>
          </div>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="overline">Describe your project *</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="An eco-friendly coffee subscription for remote teams…"
                rows={3}
                data-testid="gen-description"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="overline">Keywords</Label>
              <Input
                value={form.keywords}
                onChange={(e) => setForm({ ...form, keywords: e.target.value })}
                placeholder="coffee, remote, subscription"
                data-testid="gen-keywords"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="overline">Niche</Label>
              <Input
                value={form.niche}
                onChange={(e) => setForm({ ...form, niche: e.target.value })}
                placeholder="FMCG / SaaS / fintech"
                data-testid="gen-niche"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="overline">Style</Label>
              <div className="flex flex-wrap gap-1.5">
                {STYLES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggleIn("styles", s)}
                    className={`px-2.5 py-1 text-[11px] rounded-full border transition-colors ${
                      form.styles.includes(s)
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-border hover:bg-muted"
                    }`}
                    data-testid={`style-${s}`}
                  >
                    {CATEGORY_LABELS[s] || s}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="overline">TLDs</Label>
              <div className="flex flex-wrap gap-1.5">
                {TLDS.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleIn("tlds", t)}
                    className={`px-2.5 py-1 text-[11px] rounded-full border font-mono transition-colors ${
                      form.tlds.includes(t)
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-border hover:bg-muted"
                    }`}
                    data-testid={`tld-${t}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="overline">Count</Label>
              <Select value={String(form.count)} onValueChange={(v) => setForm({ ...form, count: Number(v) })}>
                <SelectTrigger data-testid="gen-count"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[10, 15, 20, 30, 40].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={generate} disabled={generating} className="w-full" data-testid="generate-btn">
              <Sparkle size={14} className="mr-2" />
              {generating ? "Generating…" : "Generate names"}
            </Button>
          </div>
        </Card>

        {/* Right: Results + availability + cart */}
        <div className="space-y-4">
          {/* Manual check + batch check */}
          <Card className="p-6" data-testid="availability-card">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="overline">Availability Checker</div>
                <h3 className="font-display text-lg font-bold tracking-tight">Check any domain</h3>
              </div>
              {suggestions.length > 0 && (
                <Button variant="outline" size="sm" onClick={checkAllSuggestions} disabled={checking} data-testid="check-all-btn">
                  <MagnifyingGlass size={14} className="mr-2" />
                  {checking ? "Checking…" : `Check all ${suggestions.length}`}
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Input
                value={manualInput}
                onChange={(e) => setManualInput(e.target.value)}
                placeholder="example.com, myidea.io"
                onKeyDown={(e) => e.key === "Enter" && checkManual()}
                className="font-mono text-sm"
                data-testid="manual-domain-input"
              />
              <Button variant="outline" onClick={checkManual} disabled={checking} data-testid="check-manual-btn">
                Check
              </Button>
            </div>
          </Card>

          {/* Suggestions grid */}
          {Object.keys(grouped).length > 0 ? (
            Object.entries(grouped).map(([cat, items]) => (
              <Card key={cat} className="p-6" data-testid={`suggestion-group-${cat}`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="overline">{CATEGORY_LABELS[cat] || cat}</div>
                    <h3 className="font-display text-lg font-bold tracking-tight">
                      {items.length} suggestion{items.length !== 1 ? "s" : ""}
                    </h3>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {items.map((s) => {
                    const key = s.domain.toLowerCase();
                    const ar = availability[key];
                    const canBuy = ar?.available && !ar?.is_premium;
                    const inCart = !!cart[s.domain];
                    return (
                      <div
                        key={s.domain}
                        className="border border-border rounded-md p-3 hover:border-foreground/30 transition-colors"
                        data-testid={`suggestion-${s.domain}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-mono text-sm font-semibold truncate">{s.domain}</div>
                          {availBadge(s.domain)}
                        </div>
                        {s.rationale && (
                          <div className="text-[11px] text-muted-foreground mt-1.5 line-clamp-2">{s.rationale}</div>
                        )}
                        <div className="flex items-center justify-end mt-2">
                          {canBuy ? (
                            inCart ? (
                              <Button size="sm" variant="outline" onClick={() => removeFromCart(s.domain)} data-testid={`remove-${s.domain}`}>
                                In Cart
                              </Button>
                            ) : (
                              <Button size="sm" onClick={() => addToCart(s.domain)} data-testid={`add-${s.domain}`}>
                                <ShoppingCart size={12} className="mr-1.5" /> Add
                              </Button>
                            )
                          ) : (
                            <span className="text-[10px] text-muted-foreground">
                              {ar?.is_premium ? `Premium ${ar?.premium_price ? "· $" + ar.premium_price : ""}` :
                                ar ? "Not available" : "Check first"}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            ))
          ) : (
            <Card className="p-12 text-center border-dashed grid-lines-bg" data-testid="empty-suggestions">
              <Sparkle size={28} className="mx-auto text-muted-foreground mb-3" />
              <div className="overline mb-2">No Suggestions Yet</div>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                Describe your project on the left and let GPT-5.2 generate names across brandable,
                short, keyword-rich, and premium categories.
              </p>
            </Card>
          )}

          {/* Cart */}
          {cartDomains.length > 0 && (
            <Card className="p-6 border-primary/40" data-testid="cart-card">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="overline">Checkout</div>
                  <h3 className="font-display text-lg font-bold">{cartDomains.length} domain{cartDomains.length !== 1 ? "s" : ""} in cart</h3>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setCart({})} data-testid="cart-clear">
                  Clear
                </Button>
              </div>
              <div className="border border-border rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr className="text-left">
                      <th className="px-3 py-2 overline">Domain</th>
                      <th className="px-3 py-2 overline">Years</th>
                      <th className="px-3 py-2 overline">Privacy</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {cartDomains.map((d) => (
                      <tr key={d} className="border-t border-border">
                        <td className="px-3 py-2 font-mono text-sm">{d}</td>
                        <td className="px-3 py-2">
                          <Select value={String(cart[d].years)} onValueChange={(v) => updateCart(d, { years: Number(v) })}>
                            <SelectTrigger className="h-8 w-20" data-testid={`years-${d}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {[1, 2, 3, 5, 10].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-3 py-2">
                          <Checkbox
                            checked={cart[d].whois_guard}
                            onCheckedChange={(v) => updateCart(d, { whois_guard: !!v })}
                            data-testid={`whois-${d}`}
                          />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => removeFromCart(d)}
                            className="h-7 w-7 rounded hover:bg-muted grid place-items-center"
                            data-testid={`cart-remove-${d}`}
                          >
                            <Trash size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 p-3 rounded-md bg-warning/10 border border-warning/30 text-xs flex gap-2">
                <Warning size={14} className="shrink-0 text-warning mt-0.5" />
                <div>
                  Purchases are charged to your <span className="font-semibold">Namecheap account balance</span>.
                  Make sure your balance is sufficient and your API IP is whitelisted.
                </div>
              </div>
              <Button
                onClick={purchase}
                disabled={purchasing}
                className="w-full mt-4 h-10"
                data-testid="purchase-btn"
              >
                <ShoppingCart size={16} className="mr-2" />
                {purchasing ? "Processing…" : "Pay with Account Balance"}
              </Button>

              {purchaseResult && (
                <div className="mt-4 border border-border rounded-md p-3" data-testid="purchase-result">
                  <div className="overline mb-2">Result</div>
                  <ul className="space-y-1 text-xs">
                    {purchaseResult.results.map((r) => (
                      <li key={r.domain} className="flex justify-between">
                        <span className="font-mono">{r.domain}</span>
                        {r.success
                          ? <StatusBadge variant="success" icon={CheckCircle}>registered</StatusBadge>
                          : <StatusBadge variant="danger" icon={XCircle}>{(r.message || "failed").slice(0, 40)}</StatusBadge>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
}
